<script setup lang="ts">
/**
 *
 * @name NewCertificateModal
 * @author @Jorel254
 * @version 1.0.0
 *
 * @description COMPONENTE MODAL QUE PERMITE CREAR Y EDITAR CERTIFICADOS
 *
 * responsabilidades:
 * R1: PERMITE CREAR UN NUEVO CERTIFICADO
 * R2: PERMITE EDITAR UN CERTIFICADO EXISTENTE
 * R3: PERMITE GESTIONAR LA INFORMACIÓN DE LA INSTITUCIÓN
 * R4: PERMITE GESTIONAR LA INFORMACIÓN DEL ESTUDIANTE
 * R5: PERMITE GESTIONAR LAS ASIGNATURAS DEL CERTIFICADO
 * R6: PERMITE VALIDAR LOS DATOS DEL CERTIFICADO
 * R7: PERMITE MANEJAR EL MODO DE PRUEBA
 *
 * @changelog 1.0.0 - 2025-06-17 - @Jorel254 - Versión inicial del componente
 */

import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { ICertificate, ISubject } from '@shared/types/certificate'
import {
	EGender,
	EPeriodType,
	EEntidadFederativa,
	ECertificationType,
	NOMBRE_ENTIDAD_FEDERATIVA,
	CTipoCertificacion,
	CPeriodType,
	ETipoAsignatura,
	EObservacionAsignatura,
	OBSERVACION_ASIGNATURA_DESCRIPCION
} from '@shared/types/enums'
import dateControl from '../utils/dateControl.vue'
import { ICareerResponse, ISelectedInstitution, Plane, IInstitutionConfiguration } from '@shared/types/base'
import { CERTIFICATE_DATA } from '@/constants/CCertificate'
import { certificateService } from '@/services/certificateService'
import { esFechaValida } from '@/utils/validations'
import { getIdNombreInstitucion, getInstitutionConfiguration, getInstitutionName, toFechaLocalMidnight } from '@/components/utils/functions'
import { CConfiguration } from '@/constants/CConfiguration'
import { IPerson } from '@shared/modules/curpUtilities.types'
import { verifyCurp } from '@shared/modules/curpUtilities.service'
import { Assets } from '@/types/assets'
import BaseModal from '../BaseModal.vue'
const assets = Assets.getInstance()

/**
 * @description Props del componente
 * @property {boolean} modelValue - Controla la visibilidad del modal
 * @property {ISelectedInstitution} institution - Institución seleccionada
 * @property {ICertificate} [certificate] - Certificado a editar
 * @property {boolean} isTestMode - Indica si el componente está en modo prueba
 * @property {number} currentInstitution - ID de la institución actual
 * @property {boolean} isEdit - Indica si el modal está en modo edición
 */
const props = defineProps<{
	modelValue: boolean
	institution: ISelectedInstitution
	certificate?: ICertificate
	isTestMode: boolean
	currentInstitution: string
	isEdit: boolean
}>()

/**
 * @description Emits del componente
 * @param {boolean} update:modelValue - Actualiza el valor del modal
 */
const emit = defineEmits<(e: 'update:modelValue', value: boolean) => void>()
const totalAsignaturas = ref<number>(0)
const complementarias = ref<number>(0)
const asignadasComplementarias = ref<number>(0)
const optativas = ref<number>(0)
const asignadasOptativas = ref<number>(0)
const creditosCursados = ref<number>(0)
const institutionConfiguration = ref<IInstitutionConfiguration>(CConfiguration)
// ==================== UTILIDADES ====================

/**
 * @description Función utilitaria para crear opciones de select para enums numéricos
 */
const createSelectOptions = <T extends Record<string, any>>(enumObj: T, textMap: Record<number, string>, defaultValue?: number): { value: number; text: string }[] => {
	const options: { value: number; text: string }[] = []
	if (defaultValue !== undefined) {
		options.push({ value: defaultValue, text: textMap[defaultValue] })
	}

	Object.entries(enumObj).forEach(([, value]) => {
		if (typeof value === 'number' && value !== defaultValue) {
			options.push({ value, text: textMap[value] })
		}
	})
	return options
}

/**
 * @description Función utilitaria para crear opciones de select para enums de string
 */
const createStringSelectOptions = <T extends Record<string, any>>(enumObj: T, textMap: Record<string, string>, defaultValue?: string): { value: string; text: string }[] => {
	const options: { value: string; text: string }[] = []
	if (defaultValue !== undefined) {
		options.push({ value: defaultValue, text: textMap[defaultValue] })
	}

	Object.entries(enumObj).forEach(([, value]) => {
		if (typeof value === 'string' && value !== defaultValue) {
			options.push({ value, text: textMap[value] })
		}
	})
	return options
}

/**
 * @description Función utilitaria para crear opciones de género
 */
const createGenderOptions = (): { label: string; value: EGender }[] => [
	{ label: 'Hombre', value: EGender.MALE },
	{ label: 'Mujer', value: EGender.FEMALE }
]

/**
 * @description Función utilitaria para obtener carreras válidas
 */
const getValidCarreras = (carreras: ICareerResponse[]): ICareerResponse[] => carreras.filter(c => c && typeof c.idCarrera !== 'undefined' && c.Revoes)

/**
 * @description Función utilitaria para obtener carrera seleccionada
 */
const getCarreraSeleccionada = (carreras: ICareerResponse[], idCarrera: string): ICareerResponse | undefined => {
	const carrerasValidas = getValidCarreras(carreras)
	return carrerasValidas.find(c => c.idCarrera === idCarrera)
}

/**
 * @description Función utilitaria para crear asignatura desde plan
 */
const createAsignaturaFromPlan = (planAsignatura: any): ISubject => ({
	idAsignatura: planAsignatura.idAsignatura || 0,
	claveAsignatura: planAsignatura.claveAsignatura || '',
	nombre: planAsignatura.nombre || '',
	ciclo: planAsignatura.ciclo || '',
	calificacion: planAsignatura.calificacion || '0',
	creditos: planAsignatura.creditos || '0',
	idTipoAsignatura: planAsignatura.idTipoAsignatura || ETipoAsignatura.OPTATIVA,
	idObservaciones: planAsignatura.idObservaciones || EObservacionAsignatura.NORMAL_ORDINARIO,
	observaciones: planAsignatura.observaciones || ''
})

/**
 * @description Función utilitaria para limpiar errores específicos
 */
const clearSpecificErrors = (errorType: string) => {
	errorsModal.value = errorsModal.value.filter(error => !error.includes(errorType))
}

/**
 * @description Función utilitaria para agregar error si no existe
 */
const addErrorIfNotExists = (error: string) => {
	if (!errorsModal.value.includes(error)) {
		errorsModal.value.push(error)
	}
}

/**
 * @description Función utilitaria para validar certificado según tipo
 */
const validateCertificateType = () => {
	clearSpecificErrors('certificado')
	const tipoCertificacion = certificateData.value.data.Expedicion.idTipoCertificacion
	const asignadas = certificateData.value.data.Asignaturas.asignadas
	if (tipoCertificacion === ECertificationType.TOTAL && asignadas < totalAsignaturas.value) {
		addErrorIfNotExists('El certificado total no puede tener menos asignaturas asignadas que el total de asignaturas')
	} else if (tipoCertificacion === ECertificationType.PARCIAL && (asignadas >= totalAsignaturas.value || asignadas === 0)) {
		addErrorIfNotExists('El certificado parcial no puede tener más asignaturas asignadas que el total de asignaturas y debe tener al menos una asignatura asignada')
	}
}

/**
 * @description Función utilitaria para validar el tamaño de asignaturas
 */
const validarTamanioAsignaturas = () => {
	if (listaAsignaturas.value.length < totalAsignaturas.value && certificateData.value.data.Expedicion.idTipoCertificacion === ECertificationType.TOTAL) {
		if (!errorsModal.value.includes('El total de asignaturas no coincide con el total de asignaturas capturadas')) {
			errorsModal.value.push('El total de asignaturas no coincide con el total de asignaturas capturadas')
		}
	} else {
		errorsModal.value = errorsModal.value.filter(error => error !== 'El total de asignaturas no coincide con el total de asignaturas capturadas')
	}
}

/**
 * @description Función utilitaria para cargar opciones de asignaturas
 */
const loadAsignaturasOptions = (planSeleccionado: Plane) => {
	const asignaturas = Object.values(planSeleccionado.Asignaturas).map(asignatura => createAsignaturaFromPlan(asignatura))
	totalAsignaturas.value = certificateData.value.data.Asignaturas.total ? certificateData.value.data.Asignaturas.total : planSeleccionado.totalAsignaturas
	if (!totalAsignaturas.value) {
		if (!errorsModal.value.includes('La carga del plan ha fallado. La configuración del plan no contiene el total de asignaturas requerido. Comunícate con ' + assets.vendor + '.')) {
			errorsModal.value.push('La carga del plan ha fallado. La configuración del plan no contiene el total de asignaturas requerido. Comunícate con ' + assets.vendor + '.')
		}
		return
	}
	complementarias.value = asignaturas.filter(a => a.idTipoAsignatura === ETipoAsignatura.COMPLEMENTARIA).length
	optativas.value = asignaturas.filter(a => a.idTipoAsignatura === ETipoAsignatura.OPTATIVA).length
	opcionesAsignaturas.value = asignaturas
	certificateData.value.data.Asignaturas.totalCreditos = planSeleccionado.totalCreditos
	currentPlan.value = planSeleccionado
}

/**
 * @description Función utilitaria para limpiar formulario de asignatura
 */
const clearAsignaturaForm = () => {
	asignaturas.value = {
		idAsignatura: 0,
		claveAsignatura: '',
		nombre: '',
		ciclo: '',
		calificacion: '',
		creditos: '',
		idTipoAsignatura: ETipoAsignatura.OPTATIVA,
		idObservaciones: EObservacionAsignatura.NORMAL_ORDINARIO,
		observaciones: ''
	}
	selectedAsignaturaId.value = null
	selectedObservacionId.value = null
}

const cancelarEdicion = () => {
	clearAsignaturaForm()
}

// ==================== REFERENCIAS REACTIVAS ====================

const institutionId = ref<string>(props.currentInstitution.toString())
const institutionName = ref<string>('')
const idNombreInstitucion = ref<number>(0)

/**
 * @description Computed property que controla la visibilidad del modal
 * @type {ComputedRef<boolean>}
 */
const showModal = computed({
	get: () => props.modelValue,
	set: value => emit('update:modelValue', value)
})

/**
 * @description Constante que define las pestañas del modal
 * @type {Array<{icon: string, title: string}>}
 */
const TABS = [
	{ icon: 'account_balance', title: 'Institución' },
	{ icon: 'school', title: 'Estudiante' },
	{ icon: 'list_alt', title: 'Asignaturas' }
]

/**
 * @description Referencia al valor de la pestaña actual
 * @type {Ref<string>}
 */
const value = ref(TABS[0].title)

/**
 * @description Computed property que obtiene la pestaña actual
 * @type {ComputedRef<{icon: string, title: string} | undefined>}
 */
const currentTab = computed(() => TABS.find(({ title }) => title === value.value))

/**
 * @description Referencia a los datos del certificado
 * @type {Ref<ICertificate>}
 */
const certificateData = ref<ICertificate>(JSON.parse(JSON.stringify(props.certificate || CERTIFICATE_DATA)))

/**
 * @description Referencia a la lista de asignaturas
 * @type {Ref<ISubject[]>}
 */
const listaAsignaturas = ref<ISubject[]>([])

/**
 * @description Referencia al plan actual
 * @type {Ref<Plane | null>}
 */
const currentPlan = ref<Plane | null>(null)

/**
 * @description Computed property que obtiene las carreras de la institución
 * @type {ComputedRef<ICareerResponse[]>}
 */
const carreras = computed<ICareerResponse[]>(() => props.institution.Carrera)

/**
 * @description Referencia que controla el ID de la asignatura seleccionada
 * @type {Ref<number | null>}
 */
const selectedAsignaturaId = ref<number | null>(null)

/**
 * @description Referencia que controla el ID de la observación seleccionada
 * @type {Ref<number | null>}
 */
const selectedObservacionId = ref<number | null>(null)

const idCampus = ref<string | null>(null)

/**
 * @description Referencia que controla el ID de la entidad federativa de la institución
 * @type {Ref<EEntidadFederativa | null>}
 */
const idEntidadFederativaIPES = ref<EEntidadFederativa | null>(null)

/**
 * @description Referencia que controla el ID de la entidad federativa de la expedición
 * @type {Ref<EEntidadFederativa | null>}
 */
const idEntidadFederativaExpedicion = ref<EEntidadFederativa | null>(null)

/**
 * @description Referencia que controla la visibilidad del modal de errores
 * @type {Ref<boolean>}
 */
const showErrorModal = ref(false)

/**
 * @description Referencia que almacena los mensajes de error
 * @type {Ref<string>}
 */
const errors = ref('')

/**
 * @description Referencia que almacena los errores del modal
 * @type {Ref<string[]>}
 */
const errorsModal = ref<string[]>([])

/**
 * @description Referencia que almacena los datos de una asignatura
 * @type {Ref<ISubject>}
 */
const asignaturas = ref<ISubject>({
	idAsignatura: 0,
	claveAsignatura: '',
	nombre: '',
	ciclo: '',
	calificacion: '',
	creditos: '',
	idTipoAsignatura: ETipoAsignatura.OPTATIVA,
	idObservaciones: EObservacionAsignatura.NORMAL_ORDINARIO,
	observaciones: ''
})

/**
 * @description Referencia que almacena las opciones de asignaturas
 * @type {Ref<ISubject[]>}
 */
const opcionesAsignaturas = ref<ISubject[]>([])

// Función para inicializar los datos del formulario
const initializeFormData = (certificate?: ICertificate) => {
	try {
		if (certificate && typeof certificate === 'object' && certificate.data) {
			certificateData.value = JSON.parse(JSON.stringify(certificate))
			asignadasComplementarias.value = certificate.data.Asignaturas?.Asignatura?.filter(a => a.idTipoAsignatura === ETipoAsignatura.COMPLEMENTARIA)?.length || 0
			asignadasOptativas.value = certificate.data.Asignaturas?.Asignatura?.filter(a => a.idTipoAsignatura === ETipoAsignatura.OPTATIVA)?.length || 0
			listaAsignaturas.value = certificate.data.Asignaturas?.Asignatura || []
			validarTamanioAsignaturas()
			creditosCursados.value = Number(certificateData.value.data.Asignaturas.creditosObtenidos)
			// Asegurar que idTipoCertificacion sea un número
			if (typeof certificateData.value.data.Expedicion.idTipoCertificacion === 'string') {
				certificateData.value.data.Expedicion.idTipoCertificacion = Number(certificateData.value.data.Expedicion.idTipoCertificacion)
			}

			// Cargar las opciones de asignaturas si hay un plan seleccionado
			if (certificate.data.Carrera?.clavePlan) {
				const carreraSeleccionada = getCarreraSeleccionada(carreras.value, certificate.data.Carrera.idCarrera)
				if (carreraSeleccionada) {
					const revoeSeleccionado = carreraSeleccionada.Revoes[certificate.data.Rvoe?.numero]
					if (revoeSeleccionado) {
						const planSeleccionado = revoeSeleccionado.Planes[certificate.data.Carrera.clavePlan]
						if (planSeleccionado) {
							loadAsignaturasOptions(planSeleccionado)
						}
					}
				}
			}

			idCampus.value = certificate.data.Ipes?.campus || null
			idEntidadFederativaIPES.value = certificate.data.Ipes?.idEntidadFederativa || null
			idEntidadFederativaExpedicion.value = certificate.data.Expedicion?.lugarExpedicion?.idEntidadFederativa || null
		} else {
			certificateData.value = JSON.parse(JSON.stringify(CERTIFICATE_DATA))
			listaAsignaturas.value = []
			opcionesAsignaturas.value = []
			clearAsignaturaForm()
			idCampus.value = null
			idEntidadFederativaIPES.value = null
			idEntidadFederativaExpedicion.value = null
			creditosCursados.value = 0
		}
		errorsModal.value = []
		errors.value = ''
		value.value = TABS[0].title
	} catch (error) {
		console.error('Error inicializando datos del formulario:', error)
		// Fallback a datos por defecto
		certificateData.value = JSON.parse(JSON.stringify(CERTIFICATE_DATA))
		listaAsignaturas.value = []
		errorsModal.value = []
		errors.value = ''
		value.value = TABS[0].title
	}
}

// ==================== COMPUTED PROPERTIES ====================

/**
 * @description Computed property que obtiene los RVOE de la carrera actual
 * @type {ComputedRef<Array<{value: string, text: string}>>}
 */
const revoCurrentCarrer = computed(() => {
	if (!certificateData.value.data.Carrera.idCarrera || !Array.isArray(props.institution.Carrera)) return []

	const carreraSeleccionada = getCarreraSeleccionada(carreras.value, certificateData.value.data.Carrera.idCarrera)
	if (!carreraSeleccionada) return []

	return Object.entries(carreraSeleccionada.Revoes).map(([key, value]) => ({
		value: String(key), // Asegurar que el value sea string
		text: String(value.numero) // Asegurar que el text sea string
	}))
})

/**
 * @description Computed property que obtiene los planes del RVOE actual
 * @type {ComputedRef<Array<{value: string, text: string}>>}
 */
const revoCurrentPlan = computed(() => {
	if (!certificateData.value.data.Carrera.idCarrera || !Array.isArray(props.institution.Carrera)) return []

	const carreraSeleccionada = getCarreraSeleccionada(carreras.value, certificateData.value.data.Carrera.idCarrera)
	if (!carreraSeleccionada) return []

	const revoeSeleccionado = carreraSeleccionada.Revoes[certificateData.value.data.Rvoe.numero]
	if (!revoeSeleccionado) return []

	return Object.entries(revoeSeleccionado.Planes).map(([key, value]) => ({
		value: String(key), // Asegurar que el value sea string
		text: String(value.clavePlan) // Asegurar que el text sea string
	}))
})

/**
 * @description Computed property que obtiene las opciones de entidad federativa
 * @type {ComputedRef<Array<{value: EEntidadFederativa, text: string}>>}
 */
const opcionesEntidadFederativa = computed(() => createStringSelectOptions(EEntidadFederativa, NOMBRE_ENTIDAD_FEDERATIVA, EEntidadFederativa.MEXICO))

/*
 * @description Computed property que obtiene las opciones de responsable
 * @type {ComputedRef<Array<{value: string, text: string}>>}
 */
const opcionesResponsable = computed(() => {
	const responsables = Object.values(props.institution?.Responsable ?? {}).filter((r: any) => {
		const certificates = r?.certificates
		if (certificates == null) return false
		if (Array.isArray(certificates)) return certificates.length > 0
		if (typeof certificates === 'object') return Object.keys(certificates).length > 0
		return Boolean(certificates)
	})
	return responsables.map((r: any) => ({
		value: r.curp,
		text: r.nombre + ' ' + r.primerApellido + ' ' + r.segundoApellido
	}))
})

/**
 * @description Computed property que obtiene las opciones de periodo
 * @type {ComputedRef<Array<{value: EPeriodType, text: string}>>}
 */
const opcionesPeriodo = computed(() => createSelectOptions(EPeriodType, CPeriodType, EPeriodType.SEMESTRE))

/**
 * @description Computed property que obtiene las opciones de tipo de certificación
 * @type {ComputedRef<Array<{value: ECertificationType, text: string}>>}
 */
const opcionesTipoCertificacion = computed(() => {
	const options = createSelectOptions(ECertificationType, CTipoCertificacion, ECertificationType.TOTAL)
	// Asegurar que siempre devuelva opciones válidas
	if (!options || options.length === 0) {
		return [
			{ value: ECertificationType.TOTAL, text: CTipoCertificacion[ECertificationType.TOTAL] },
			{ value: ECertificationType.PARCIAL, text: CTipoCertificacion[ECertificationType.PARCIAL] }
		]
	}
	return options
})

/**
 * @description Computed property que obtiene las opciones de observaciones
 * @type {ComputedRef<Array<{value: EObservacionAsignatura, text: string}>>}
 */
const opcionesObservaciones = computed(() => createSelectOptions(EObservacionAsignatura, OBSERVACION_ASIGNATURA_DESCRIPCION, EObservacionAsignatura.NORMAL_ORDINARIO))

/**
 * @description Opciones de género
 * @type {Array<{label: string, value: EGender}>}
 */
const opcionesGenero = createGenderOptions()

const isInstitutionDataComplete = computed(() => {
	const data = certificateData.value.data
	return (
		data.Ipes.campus &&
		data.Ipes.idEntidadFederativa &&
		data.Ipes.Responsable.curp &&
		data.Carrera.idCarrera &&
		data.Rvoe.numero &&
		data.Carrera.clavePlan &&
		data.Carrera.idTipoPeriodo &&
		data.Expedicion.idTipoCertificacion &&
		data.Expedicion.fecha &&
		data.Expedicion.lugarExpedicion.idEntidadFederativa
	)
})

const isStudentDataComplete = computed(() => {
	const data = certificateData.value.data.Alumno
	return data.numeroControl && data.curp && data.nombre && data.primerApellido && data.fechaNacimiento && data.idGenero
})

/**
 * @description Función que actualiza la fecha de nacimiento
 * @param {any} value - Nueva fecha de nacimiento
 */
const updateFechaNacimiento = (value: any) => {
	certificateData.value.data.Alumno.fechaNacimiento = toFechaLocalMidnight(value)
	getCurpError(certificateData.value.data.Alumno.curp)
}

/**
 * @description Función que actualiza la fecha de expedición
 * @param {any} value - Nueva fecha de expedición
 */
const updateFechaExpedicion = (value: any) => {
	const fechaExpedicion = new Date(value)
	const fechaActual = new Date()
	fechaExpedicion.setHours(0, 0, 0, 6)
	fechaActual.setHours(0, 0, 0, 6)
	if (fechaExpedicion >= fechaActual) {
		showErrorModal.value = true
		addErrorIfNotExists('La fecha de expedición no puede ser hoy ni posterior')
	} else if (Number.isNaN(new Date(value).getTime())) {
		showErrorModal.value = true
		addErrorIfNotExists('La fecha de expedición no es válida')
		value = new Date(new Date().setDate(new Date().getDate() - 1))
	} else {
		clearSpecificErrors('fecha de expedición')
	}
	certificateData.value.data.Expedicion.fecha = toFechaLocalMidnight(value)
}

/**
 * @description Función que verifica si una fecha es válida
 * @param {Date} [date] - Fecha a verificar
 * @returns {boolean} true si la fecha es válida
 */
const verificarFecha = (date?: Date) => esFechaValida(date)

/**
 * @description Función que cierra el modal
 */
const closeModal = () => {
	showModal.value = false
}

/**
 * @description Función que agrega una asignatura
 */
const agregarAsignatura = () => {
	if (selectedAsignaturaId.value === null || selectedObservacionId.value === null || asignaturas.value.ciclo === '' || asignaturas.value.calificacion === '') {
		showErrorModal.value = true
		errors.value = 'No se puede agregar una asignatura sin datos'
		return
	}
	const calificacion = errorsModal.value.find(error => error.includes('calificación'))
	const ciclo = errorsModal.value.find(error => error.includes('ciclo escolar'))
	if (calificacion || ciclo) {
		showErrorModal.value = true
		errors.value = 'No se puede agregar una asignatura con datos inválidos'
		return
	}

	// Buscar si ya existe la asignatura
	const asignaturaExistente = listaAsignaturas.value.find(a => a.idAsignatura === selectedAsignaturaId.value)
	if (asignaturaExistente) {
		// Si existe, verificar si hay cambios
		const hayCambios =
			asignaturaExistente.ciclo !== asignaturas.value.ciclo ||
			asignaturaExistente.calificacion !== asignaturas.value.calificacion ||
			asignaturaExistente.idObservaciones !== asignaturas.value.idObservaciones

		if (hayCambios) {
			// Si hay cambios, actualizar la asignatura existente
			Object.assign(asignaturaExistente, asignaturas.value)
		} else {
			// Si no hay cambios, mostrar mensaje
			showErrorModal.value = true
			errors.value = 'La asignatura ya existe y no se han detectado cambios'
			return
		}
	} else {
		// Si no existe, agregar nueva asignatura
		listaAsignaturas.value.push({ ...asignaturas.value })
		if (listaAsignaturas.value.length === totalAsignaturas.value) {
			clearSpecificErrors('certificado')
		}
		if (asignaturas.value.idTipoAsignatura === ETipoAsignatura.COMPLEMENTARIA) {
			asignadasComplementarias.value += 1
		} else if (asignaturas.value.idTipoAsignatura === ETipoAsignatura.OPTATIVA) {
			asignadasOptativas.value += 1
			certificateData.value.data.Asignaturas.asignadas += 1
		} else {
			certificateData.value.data.Asignaturas.asignadas += 1
		}
	}

	clearAsignaturaForm()
}

const editarAsignatura = (idAsignatura: number) => {
	if (!opcionesAsignaturas.value.length) {
		showErrorModal.value = true
		errors.value = 'Primero selecciona un plan para ver las asignaturas disponibles.'
		return
	}
	const asignatura = listaAsignaturas.value.find(a => a.idAsignatura === idAsignatura)
	if (!asignatura) return

	// Asegurar que la asignatura esté en las opciones disponibles para poder editarla
	const asignaturaEnOpciones = opcionesAsignaturas.value.find(a => a.idAsignatura === idAsignatura)
	if (!asignaturaEnOpciones) {
		// Si la asignatura no está en las opciones, agregarla temporalmente
		opcionesAsignaturas.value.push({
			idAsignatura: asignatura.idAsignatura,
			claveAsignatura: asignatura.claveAsignatura,
			nombre: asignatura.nombre,
			ciclo: asignatura.ciclo,
			calificacion: asignatura.calificacion,
			creditos: asignatura.creditos,
			idTipoAsignatura: asignatura.idTipoAsignatura,
			idObservaciones: asignatura.idObservaciones,
			observaciones: asignatura.observaciones
		})
	}

	asignaturas.value = JSON.parse(JSON.stringify(asignatura))
	selectedAsignaturaId.value = asignatura.idAsignatura
	selectedObservacionId.value = Number(asignatura.idObservaciones)
}

const eliminarAsignatura = (idAsignatura: number) => {
	const asignatura = listaAsignaturas.value.find(a => a.idAsignatura === idAsignatura)
	if (!asignatura) return

	if (asignatura.idTipoAsignatura === ETipoAsignatura.COMPLEMENTARIA && asignadasComplementarias.value > 0) {
		asignadasComplementarias.value -= 1
	} else if (asignatura.idTipoAsignatura === ETipoAsignatura.OPTATIVA && asignadasOptativas.value > 0) {
		asignadasOptativas.value -= 1
		certificateData.value.data.Asignaturas.asignadas -= 1
	} else {
		certificateData.value.data.Asignaturas.asignadas -= 1
	}

	// Usar nextTick para asegurar que el cambio se detecte
	nextTick(() => {
		listaAsignaturas.value = listaAsignaturas.value.filter(a => a.idAsignatura !== idAsignatura)
		validateCertificateType()
	})
}

const guardarCertificado = async () => {
	creditosCursados.value = 0
	certificateData.value.data.Expedicion.lugarExpedicion.entidadFederativa = NOMBRE_ENTIDAD_FEDERATIVA[certificateData.value.data.Expedicion.lugarExpedicion.idEntidadFederativa]
	certificateData.value.data.Asignaturas.Asignatura = listaAsignaturas.value
	institutionName.value = await getInstitutionName(institutionId.value)
	idNombreInstitucion.value = Number(await getIdNombreInstitucion(institutionId.value))
	certificateData.value.data.Ipes.idNombreInstitucion = Number(
		certificateData.value.data.Ipes.idNombreInstitucion === 0 ? idNombreInstitucion.value : certificateData.value.data.Ipes.idNombreInstitucion
	)
	certificateData.value.data.Ipes.nombreInstitucion = institutionName.value
	const creditosObtenidos = listaAsignaturas.value.reduce((acc, asignatura) => acc + Number(asignatura.creditos), 0)
	certificateData.value.data.Asignaturas.creditosObtenidos = creditosObtenidos.toString()
	certificateData.value.data.Asignaturas.total = totalAsignaturas.value
	certificateService.saveData(certificateData.value, currentPlan.value as Plane, Number(props.currentInstitution), props.isTestMode, props.isEdit).then(() => {
		emit('update:modelValue', false)
		showModal.value = false
	})
}

const validarCiclo = (value: string) => {
	if (!value) return true
	const cicloRegex = /^\d{4}-\d{1}$/
	if (!cicloRegex.test(value)) {
		addErrorIfNotExists('El ciclo escolar debe tener el formato 0000-0')
		return false
	}
	clearSpecificErrors('ciclo escolar')
	return true
}

const validarCalificacion = (value: string) => {
	if (!value) return true
	if (value.toLocaleUpperCase() === 'ACREDITADA' && selectedObservacionId.value === EObservacionAsignatura.EQUIVALENCIA_ESTUDIOS) {
		clearSpecificErrors('calificación')
		return true
	}
	const numero = Number(value)
	if (Number.isNaN(numero)) {
		addErrorIfNotExists('La calificación debe ser un número válido')
		return false
	}
	if (numero < Number(certificateData.value.data.Carrera.calificacionMinimaAprobatoria) || numero > 10) {
		addErrorIfNotExists(`La calificación no puede ser menor a ${certificateData.value.data.Carrera.calificacionMinimaAprobatoria} o mayor a 10`)
		return false
	}

	const partes = value.split('.')
	if (partes.length > 1 && partes[1].length !== Number(institutionConfiguration.value.certificates.decimals)) {
		addErrorIfNotExists(`La calificación debe tener ${institutionConfiguration.value.certificates.decimals} decimales o ser un número entero`)
		return false
	}

	clearSpecificErrors('calificación')
	return true
}

const validarNumeroControl = (value: string) => {
	if (!value) return 'La matrícula es requerida'
	if (value.includes('/')) return 'La matrícula no puede contener barras'
	return true
}

const getCurpError = (curp: string) => {
	const curpValue = curp.toUpperCase()
	if (curpValue === 'EXTRANJERO') {
		clearSpecificErrors('CURP')
		return
	}
	const birthday = new Date(certificateData.value.data.Alumno.fechaNacimiento)

	const person: IPerson = {
		name: certificateData.value.data.Alumno.nombre,
		firstLastName: certificateData.value.data.Alumno.primerApellido,
		secondLastName: certificateData.value.data.Alumno.segundoApellido,
		birthday: birthday,
		gender: Number(certificateData.value.data.Alumno.idGenero)
	}
	console.log(person)
	console.log(verifyCurp(curp, person))
	if (verifyCurp(curp, person).code === 2) {
		addErrorIfNotExists('La CURP no coincide con los datos del estudiante')
	} else {
		clearSpecificErrors('CURP')
	}
}
const validarFormularioCompleto = () => {
	// Obtener errores de cada sección
	const erroresEscuela = validarDatosEscuela()
	const erroresResponsable = validarDatosResponsable()
	const erroresCarrera = validarDatosCarrera()
	const erroresExpedicion = validarDatosExpedicion()
	const erroresEstudiante = validarDatosEstudiante()

	errorsModal.value = errorsModal.value.filter(error => error !== 'Todos los campos son requeridos')

	if (!erroresEscuela && !erroresResponsable && !erroresCarrera && !erroresExpedicion && !erroresEstudiante) {
		if (errorsModal.value.length === 0) {
			errorsModal.value = []
		}
	} else {
		const errorMessage = 'Todos los campos son requeridos'
		if (!errorsModal.value.includes(errorMessage)) {
			errorsModal.value.push(errorMessage)
		}
	}
}

const validarDatosEscuela = (): boolean => {
	const datosEscuela = [idCampus.value?.trim(), idEntidadFederativaIPES.value?.trim()]

	if (datosEscuela.some(dato => !dato)) {
		return true
	}
	return false
}
const validarDatosResponsable = (): boolean => {
	const datosResponsable = [certificateData.value.data.Ipes.Responsable.curp?.trim()]

	if (datosResponsable.some(dato => !dato)) {
		return true
	}
	return false
}
const validarDatosCarrera = (): boolean => {
	const datosCarrera = [
		certificateData.value.data.Carrera.idCarrera?.toString().trim(),
		certificateData.value.data.Rvoe.numero.toString().trim(),
		certificateData.value.data.Carrera.clavePlan?.toString().trim(),
		certificateData.value.data.Carrera.idTipoPeriodo.toString().trim()
	]

	if (datosCarrera.some(dato => !dato)) {
		return true
	}
	return false
}
const validarDatosExpedicion = (): boolean => {
	const datosExpedicion = [certificateData.value.data.Expedicion.idTipoCertificacion.toString().trim(), idEntidadFederativaExpedicion.value?.trim()]

	if (datosExpedicion.some(dato => !dato)) {
		return true
	}
	return false
}
const validarDatosEstudiante = (): boolean => {
	const datosEstudiante = [
		certificateData.value.data.Alumno.curp?.trim(),
		certificateData.value.data.Alumno.numeroControl?.trim(),
		certificateData.value.data.Alumno.nombre?.trim(),
		certificateData.value.data.Alumno.primerApellido?.trim(),
		certificateData.value.data.Alumno.fechaNacimiento?.trim(),
		certificateData.value.data.Alumno.idGenero?.trim()
	]

	if (datosEstudiante.some(dato => !dato)) {
		return true
	}
	return false
}
// ==================== WATCHERS ====================

/**
 * @description Watcher que actualiza los datos del certificado cuando cambia el prop certificate
 */
watch(
	() => props.certificate,
	async newVal => {
		if (newVal) {
			initializeFormData(newVal)
			validarFormularioCompleto()
			institutionConfiguration.value = await getInstitutionConfiguration(props.currentInstitution)
		} else {
			initializeFormData()
			validarFormularioCompleto()
			institutionConfiguration.value = await getInstitutionConfiguration(props.currentInstitution)
		}
	},
	{ immediate: true }
)

/**
 * @description Hook que se ejecuta al montar el componente
 */
onMounted(async () => {
	if (props.certificate) {
		initializeFormData(props.certificate)
		validarFormularioCompleto()
		institutionConfiguration.value = await getInstitutionConfiguration(props.currentInstitution)
	}
	institutionName.value = await getInstitutionName(institutionId.value)
	certificateData.value.data.Ipes = {
		...certificateData.value.data.Ipes,
		idNombreInstitucion: Number(certificateData.value.data.Ipes.idNombreInstitucion),
		nombreInstitucion: institutionName.value
	}
})

watch(
	() => props.currentInstitution,
	async newVal => {
		institutionId.value = newVal.toString()
		validarFormularioCompleto()
		institutionConfiguration.value = await getInstitutionConfiguration(props.currentInstitution)
	}
)

watch(
	() => institutionId.value,
	async () => {
		institutionName.value = await getInstitutionName(institutionId.value)
		certificateData.value.data.Ipes.idNombreInstitucion = Number(certificateData.value.data.Ipes.idNombreInstitucion)
		certificateData.value.data.Ipes.nombreInstitucion = institutionName.value
		validarFormularioCompleto()
	}
)

watch(
	() => certificateData.value.data.Ipes.Responsable.curp,
	value => {
		try {
			if (!value || !props.institution.Responsable || !props.institution.Responsable[value]) {
				return
			}

			const responsable = props.institution.Responsable[value]

			Object.keys(props.institution.Responsable).forEach(key => {
				if (key === value) {
					certificateData.value.data.Ipes.Responsable.nombre = responsable.nombre
					certificateData.value.data.Ipes.Responsable.primerApellido = responsable.primerApellido
					certificateData.value.data.Ipes.Responsable.segundoApellido = responsable.segundoApellido
					certificateData.value.data.Ipes.Responsable.idCargo = responsable.certificates?.idCargo
					certificateData.value.data.Ipes.Responsable.cargo = responsable.certificates?.cargo
				}
			})

			certificateData.value.data.FirmaResponsables = {
				[value]: {
					nombre: responsable.nombre,
					primerApellido: responsable.primerApellido,
					segundoApellido: responsable.segundoApellido,
					abrTitulo: '',
					curp: responsable.curp,
					certificadoResponsable: responsable.cer?.certificadoResponsable,
					noCertificadoResponsable: responsable.cer?.noCertificadoResponsable,
					sello: responsable.cer?.signature,
					idCargo: responsable.certificates?.idCargo,
					cargo: responsable.certificates?.cargo
				}
			}
		} catch (error) {
			console.error('Error en watcher de responsable:', error)
		}
	}
)

watch(
	() => certificateData.value.data.Carrera.idCarrera,
	(newValue, oldValue) => {
		try {
			if (newValue !== oldValue) {
				if (!newValue || !Array.isArray(carreras.value) || carreras.value.length === 0) return

				if (newValue !== oldValue && oldValue !== undefined && oldValue !== null && oldValue !== '') {
					listaAsignaturas.value = []
					asignaturas.value = {
						idAsignatura: 0,
						claveAsignatura: '',
						nombre: '',
						ciclo: '',
						calificacion: '',
						creditos: '',
						idTipoAsignatura: ETipoAsignatura.OBLIGATORIA,
						idObservaciones: EObservacionAsignatura.NORMAL_ORDINARIO,
						observaciones: ''
					}
					certificateData.value.data.Asignaturas.asignadas = 0
				}
				const carreraSeleccionada = getCarreraSeleccionada(carreras.value, newValue)
				if (!carreraSeleccionada) return

				certificateData.value.data.Carrera.nombreCarrera = carreraSeleccionada.nombreCarrera || ''
				certificateData.value.data.Carrera.cveCarrera = carreraSeleccionada.claveCarrera || ''
				certificateData.value.data.Carrera.idNivelEstudios = carreraSeleccionada.idNivelEstudios || 0

				const nuevosRevoes = revoCurrentCarrer.value.map(opt => opt.value)
				const numeroRvoeActual = certificateData.value.data.Rvoe.numero

				// Convertir ambos a string para comparación robusta
				const numeroRvoeActualStr = String(numeroRvoeActual)
				const nuevosRevoesStr = nuevosRevoes.map(String)

				// Solo cambiar el RVOE si realmente no está disponible
				if (!nuevosRevoesStr.includes(numeroRvoeActualStr)) {
					console.warn(`RVOE ${numeroRvoeActual} no disponible en carrera ${newValue}, cambiando a ${nuevosRevoes[0]}`)
					certificateData.value.data.Rvoe.numero = nuevosRevoes[0] || ''
				}

				const nuevosPlanes = revoCurrentPlan.value.map(opt => opt.value)
				const clavePlanActual = certificateData.value.data.Carrera.clavePlan

				// Convertir ambos a string para comparación robusta
				const clavePlanActualStr = String(clavePlanActual)
				const nuevosPlanesStr = nuevosPlanes.map(String)

				// Solo cambiar el plan si realmente no está disponible
				if (!nuevosPlanesStr.includes(clavePlanActualStr)) {
					console.warn(`Plan ${clavePlanActual} no disponible en carrera ${newValue}, cambiando a ${nuevosPlanes[0]}`)
					certificateData.value.data.Carrera.clavePlan = nuevosPlanes[0] || ''
				}
			}
		} catch (error) {
			console.error('Error en watcher de carrera:', error)
		}
	}
)

watch(
	() => certificateData.value.data.Rvoe.numero,
	(newValue, oldValue) => {
		try {
			if (newValue === oldValue) return

			// Evitar ejecución durante la inicialización
			if (oldValue === undefined || oldValue === null) {
				return
			}

			const carreraSeleccionada = getCarreraSeleccionada(carreras.value, certificateData.value.data.Carrera.idCarrera)
			if (!carreraSeleccionada) return

			const revoeSeleccionado = carreraSeleccionada.Revoes[newValue]
			if (!revoeSeleccionado) return

			// Actualizar solo las propiedades del RVOE, no el número para evitar bucles
			certificateData.value.data.Carrera.calificacionMaxima = revoeSeleccionado.calificacionMaxima
			certificateData.value.data.Carrera.calificacionMinima = revoeSeleccionado.calificacionMinima
			certificateData.value.data.Carrera.calificacionMinimaAprobatoria = revoeSeleccionado.calificacionMinimaAprobatoria
			certificateData.value.data.Rvoe.fechaExpedicion = revoeSeleccionado.fechaExpedicion

			// Actualizar el plan cuando cambia el RVOE
			const nuevosPlanes = revoCurrentPlan.value.map(opt => opt.value)
			const clavePlanActual = certificateData.value.data.Carrera.clavePlan

			// Convertir ambos a string para comparación robusta
			const clavePlanActualStr = String(clavePlanActual)
			const nuevosPlanesStr = nuevosPlanes.map(String)

			// Solo cambiar el plan si realmente no está disponible
			if (!nuevosPlanesStr.includes(clavePlanActualStr)) {
				console.warn(`Plan ${clavePlanActual} no disponible en RVOE ${newValue}, cambiando a ${nuevosPlanes[0]}`)
				certificateData.value.data.Carrera.clavePlan = nuevosPlanes[0] || ''
			}
		} catch (error) {
			console.error('Error en watcher de RVOE:', error)
		}
	}
)

watch(
	() => certificateData.value.data.Carrera.idTipoPeriodo,
	newVal => {
		let val = newVal
		if (typeof val === 'string') {
			val = Number(val)
			certificateData.value.data.Carrera.idTipoPeriodo = val
		}
		const periodType = CPeriodType[val]
		if (periodType) {
			certificateData.value.data.Carrera.tipoPeriodo = periodType as keyof typeof EPeriodType
		}
		validarFormularioCompleto()
	},
	{ immediate: true }
)

watch(
	() => certificateData.value.data.Expedicion.idTipoCertificacion,
	newValue => {
		let val = newValue
		if (typeof val === 'string') {
			val = Number(val)
			certificateData.value.data.Expedicion.idTipoCertificacion = val
		}

		// Asegurar que el valor sea válido
		if (val !== ECertificationType.TOTAL && val !== ECertificationType.PARCIAL) {
			// Si el valor no es válido, establecer el valor por defecto
			certificateData.value.data.Expedicion.idTipoCertificacion = ECertificationType.TOTAL
			val = ECertificationType.TOTAL
		}

		const tipoCertificacion = CTipoCertificacion[val]
		if (tipoCertificacion) {
			certificateData.value.data.Expedicion.tipoCertificacion = tipoCertificacion as keyof typeof ECertificationType
		}

		validateCertificateType()
		validarFormularioCompleto()
		validarTamanioAsignaturas()
	}
)

watch(
	() => certificateData.value.data.Carrera.clavePlan,
	(newClavePlan: string | number) => {
		const carreraSeleccionada = getCarreraSeleccionada(carreras.value, certificateData.value.data.Carrera.idCarrera)
		if (!carreraSeleccionada) return

		const revoeSeleccionado = carreraSeleccionada.Revoes[certificateData.value.data.Rvoe.numero]
		if (!revoeSeleccionado) return

		const planSeleccionado = revoeSeleccionado.Planes[newClavePlan]
		if (!planSeleccionado) return

		loadAsignaturasOptions(planSeleccionado)
		validarFormularioCompleto()
	}
)

watch(
	() => idEntidadFederativaExpedicion.value,
	(newValue, oldValue) => {
		if (newValue !== oldValue) {
			const entidadFederativa = NOMBRE_ENTIDAD_FEDERATIVA[newValue as EEntidadFederativa]
			if (entidadFederativa) {
				certificateData.value.data.Expedicion.lugarExpedicion.idEntidadFederativa = newValue as EEntidadFederativa
				certificateData.value.data.Expedicion.lugarExpedicion.entidadFederativa = entidadFederativa
			}
		}
		validarFormularioCompleto()
	},
	{ immediate: true }
)

watch(
	() => idCampus.value,
	(newValue, oldValue) => {
		if (newValue !== oldValue) {
			certificateData.value.data.Ipes.campus = newValue || ''
			const campusValido = props.institution.Campus.filter(c => c && typeof c.idCampus !== 'undefined')
			const campus = campusValido.find(c => c.campus === newValue)
			if (!campus) return
			certificateData.value.data.Ipes.campus = campus.campus
			certificateData.value.data.Ipes.idCampus = campus.claveCampus
		}
		validarFormularioCompleto()
	}
)

watch(
	() => idEntidadFederativaIPES.value,
	(newValue, oldValue) => {
		if (newValue !== oldValue) {
			certificateData.value.data.Ipes.idEntidadFederativa = newValue as EEntidadFederativa
			certificateData.value.data.Ipes.entidadFederativa = NOMBRE_ENTIDAD_FEDERATIVA[newValue as EEntidadFederativa]
		}
		validarFormularioCompleto()
	},
	{ immediate: true }
)

watch(
	() => selectedObservacionId.value,
	(newValue, oldValue) => {
		if (newValue !== oldValue) {
			asignaturas.value.idObservaciones = Number(newValue)
			asignaturas.value.observaciones = OBSERVACION_ASIGNATURA_DESCRIPCION[asignaturas.value.idObservaciones]
			validarCalificacion(asignaturas.value.calificacion)
		}
		validarFormularioCompleto()
	}
)

watch(
	() => selectedAsignaturaId.value,
	(newValue, oldValue) => {
		if (newValue !== oldValue) {
			const asignaturaSeleccionada = opcionesAsignaturas.value.find(a => a.idAsignatura === Number(newValue))
			if (!asignaturaSeleccionada) return
			asignaturas.value.idAsignatura = asignaturaSeleccionada.idAsignatura
			asignaturas.value.claveAsignatura = asignaturaSeleccionada.claveAsignatura
			asignaturas.value.nombre = asignaturaSeleccionada.nombre
			asignaturas.value.creditos = asignaturaSeleccionada.creditos
			asignaturas.value.idTipoAsignatura = asignaturaSeleccionada.idTipoAsignatura
		}
		validarFormularioCompleto()
	}
)

watch(
	() => showModal.value,
	isOpen => {
		if (isOpen && props.certificate) {
			initializeFormData(props.certificate)
			// Asegurar que idTipoCertificacion sea un número válido
			if (typeof certificateData.value.data.Expedicion.idTipoCertificacion === 'string') {
				certificateData.value.data.Expedicion.idTipoCertificacion = Number(certificateData.value.data.Expedicion.idTipoCertificacion)
			}
			if (certificateData.value.data.Expedicion.idTipoCertificacion !== ECertificationType.TOTAL && certificateData.value.data.Expedicion.idTipoCertificacion !== ECertificationType.PARCIAL) {
				certificateData.value.data.Expedicion.idTipoCertificacion = ECertificationType.TOTAL
			}
			validarFormularioCompleto()
			getCurpError(certificateData.value.data.Alumno.curp)
			validateCertificateType()
			validarNumeroControl(certificateData.value.data.Alumno.numeroControl)
		}
		if (isOpen && !props.certificate) {
			initializeFormData()
		}
	}
)

watch(
	() => certificateData.value.data.Asignaturas.total,
	() => {
		certificateData.value.data.Asignaturas.total = totalAsignaturas.value
		validateCertificateType()
		validarFormularioCompleto()
	}
)

// Watcher optimizado para validación de CURP
watch(
	() => certificateData.value.data.Alumno,
	() => {
		try {
			if (certificateData.value?.data?.Alumno?.curp) {
				certificateData.value.data.Alumno.curp = certificateData.value.data.Alumno.curp.toUpperCase()
				getCurpError(certificateData.value.data.Alumno.curp)
			}
		} catch (error) {
			console.error('Error en watcher de CURP:', error)
		}
		validarFormularioCompleto()
	},
	{ deep: true }
)

// Watcher para recomputar opcionesAsignaturas cuando las carreras estén disponibles después de recargar la página
watch(
	() => carreras.value,
	newCarreras => {
		// Solo ejecutar si hay carreras disponibles y hay un plan seleccionado
		if (newCarreras && newCarreras.length > 0 && certificateData.value.data.Carrera.clavePlan) {
			const carreraSeleccionada = getCarreraSeleccionada(newCarreras, certificateData.value.data.Carrera.idCarrera)
			if (carreraSeleccionada) {
				const revoeSeleccionado = carreraSeleccionada.Revoes[certificateData.value.data.Rvoe.numero]
				if (revoeSeleccionado) {
					const planSeleccionado = revoeSeleccionado.Planes[certificateData.value.data.Carrera.clavePlan]
					if (planSeleccionado) {
						loadAsignaturasOptions(planSeleccionado)
					}
				}
			}
		}
		validarFormularioCompleto()
	},
	{ immediate: true }
)

/**
 * @description Computed property que obtiene las asignaturas disponibles (no seleccionadas)
 * @type {ComputedRef<ISubject[]>}
 */
const asignaturasDisponibles = computed(() => {
	const asignaturasSeleccionadas = listaAsignaturas.value.map(a => a.idAsignatura)
	// Si se está editando una asignatura, incluirla en las opciones disponibles
	const asignaturaEnEdicion = selectedAsignaturaId.value
	const asignaturasFiltradas = opcionesAsignaturas.value.filter(asignatura => {
		// Si la asignatura está siendo editada, incluirla
		if (asignaturaEnEdicion && asignatura.idAsignatura === asignaturaEnEdicion) {
			return true
		}
		// Si no está siendo editada, aplicar el filtro normal
		return !asignaturasSeleccionadas.includes(asignatura.idAsignatura)
	})
	return asignaturasFiltradas
})

// Watch adicional para la longitud del array (más confiable)
watch(
	() => listaAsignaturas.value.length,
	() => {
		validateCertificateType()
		validarFormularioCompleto()
		validarTamanioAsignaturas()
	},
	{ immediate: false }
)

const isNotEmpty = (value: any) => value !== '' && value !== null && value !== undefined

// Create a function that takes idAsignatura as parameter and returns a boolean
const existeAsignatura = (idAsignatura: number | null) => Boolean(idAsignatura && listaAsignaturas.value.some(a => a.idAsignatura === idAsignatura))
</script>

<template>
	<VaModal v-model="showModal" hide-default-actions noDismiss noEscDismiss noOutsideDismiss max-width="1500px" background-color="transparent">
		<VaCard max-w-4xl mx-auto>
			<!-- Header con gradiente -->
			<VaCardTitle :class="[props.isTestMode ? 'bg-gradient-paquete-test' : 'bg-gradient-paquete', 'rounded-t-xl']">
				<div class="row align-items-center">
					<div class="col-md-11 text-left">
						<VaIcon name="school" class="text-white" size="large" />
						<div>
							<h2 class="text-xl font-bold text-white m-0">
								{{ props.isEdit ? 'Editar Certificado' : 'Nuevo Certificado' }}
							</h2>
							<p class="text-sm text-blue-100 m-0 text-white">
								{{ institutionName }}
							</p>
						</div>
					</div>
					<div class="col-md-1 text-right ml2">
						<VaButton round icon="cancel" :color="!props.isTestMode ? '#1e3a8a' : '#7f1d1d'" type="button" @click="closeModal" style="color: white; font-weight: bold" />
					</div>
				</div>
			</VaCardTitle>

			<VaCardContent class="p-6">
				<!-- Tabs mejorados -->
				<VaTabs v-model="value" grow class="mb-6">
					<template #tabs>
						<VaTab
							v-for="tab in TABS"
							:key="tab.title"
							:name="tab.title"
							:disabled="(tab.title === 'Estudiante' && !isInstitutionDataComplete) || (tab.title === 'Asignaturas' && (!isInstitutionDataComplete || !isStudentDataComplete))"
							class="flex items-center gap-2">
							<VaIcon :name="tab.icon" size="small" class="title-icon" />
							{{ tab.title }}
						</VaTab>
					</template>
				</VaTabs>

				<!-- Contenido de las pestañas -->
				<div v-if="currentTab?.title === 'Institución'" class="space-y-6">
					<!-- Sección: Datos de la escuela -->
					<div class="package-section">
						<div class="section-title">
							<VaIcon name="account_balance" class="title-icon" />
							Datos de la escuela
						</div>
						<div class="package-grid">
							<div class="package-card">
								<div class="gap-3 mb-4">
									<div class="signer-avatar">
										<VaIcon name="business" class="signer-icon" />
									</div>
									<div>
										<h3 class="font-semibold text-lg m-0">Institución</h3>
										<p class="text-gray-600 mt-2">{{ institutionName }}</p>
										<p class="text-gray-600 mt-2">Campus y entidad</p>
									</div>
								</div>
								<div class="mb-3">
									<VaSelect
										style="width: 100%"
										label="Campus"
										v-model="idCampus"
										:options="institution.Campus"
										value-by="campus"
										text-by="campus"
										no-options-text="No se encontraron elementos"
										placeholder="Selecciona un campus" />
									<span v-if="!isNotEmpty(idCampus)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
								</div>
								<div class="mb-3">
									<VaSelect
										style="width: 100%"
										label="Entidad del Campus"
										v-model="idEntidadFederativaIPES"
										:options="opcionesEntidadFederativa"
										value-by="value"
										text-by="text"
										no-options-text="No se encontraron elementos" />
									<span v-if="!isNotEmpty(idEntidadFederativaIPES)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
								</div>
							</div>
						</div>
					</div>

					<!-- Sección: Datos del Responsable -->
					<div class="signers-section">
						<div class="section-title">
							<VaIcon name="groups" class="title-icon" />
							Datos del Responsable
						</div>
						<div class="signers-grid">
							<div class="signer-card">
								<div class="gap-3 mb-4">
									<div class="signer-avatar mb-2">
										<VaIcon name="person" class="signer-icon" />
									</div>
									<div>
										<h3 class="font-semibold text-lg m-0">Responsable de Firma</h3>
										<p class="text-gray-600 m-0">Selecciona el responsable</p>
									</div>
								</div>
								<div class="mb-3">
									<VaSelect
										style="width: 100%"
										label="Responsable"
										v-model="certificateData.data.Ipes.Responsable.curp"
										:options="opcionesResponsable"
										value-by="value"
										text-by="text"
										no-options-text="No se encontraron elementos" />
									<span v-if="!isNotEmpty(certificateData.data.Ipes.Responsable.curp)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
								</div>
							</div>
						</div>
					</div>

					<!-- Sección: Datos de la carrera -->
					<div>
						<div class="section-title">
							<VaIcon name="school" class="title-icon" />
							Datos de la carrera
						</div>
						<div class="package-grid">
							<div class="package-card">
								<div class="gap-3 mb-4">
									<div class="signer-avatar mb-2">
										<VaIcon name="collections_bookmark" class="signer-icon" />
									</div>
									<div>
										<h3 class="font-semibold text-lg m-0">Carrera y RVOE</h3>
										<p class="text-gray-600 m-0">Información académica</p>
									</div>
								</div>
								<div class="mb-3">
									<VaSelect
										style="width: 100%"
										label="Carrera"
										v-model="certificateData.data.Carrera.idCarrera"
										:options="carreras"
										value-by="idCarrera"
										text-by="nombreCarrera"
										no-options-text="No se encontraron elementos"
										class="mb-3" />
									<span v-if="!isNotEmpty(certificateData.data.Carrera.idCarrera)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
								</div>
								<div class="mb-3">
									<VaSelect
										style="width: 100%"
										label="Número de RVOE"
										v-model="certificateData.data.Rvoe.numero"
										:options="revoCurrentCarrer"
										value-by="value"
										text-by="text"
										no-options-text="No se encontraron elementos" />
									<span v-if="!isNotEmpty(certificateData.data.Rvoe.numero)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
								</div>
							</div>

							<div class="package-card">
								<div class="gap-3 mb-4">
									<div class="signer-avatar mb-2">
										<VaIcon name="description" class="signer-icon" />
									</div>
									<div>
										<h3 class="font-semibold text-lg m-0">Plan y Periodo</h3>
										<p class="text-gray-600 m-0">Configuración académica</p>
									</div>
								</div>
								<div class="mb-3">
									<VaSelect
										style="width: 100%"
										label="Plan"
										v-model="certificateData.data.Carrera.clavePlan"
										:options="revoCurrentPlan"
										value-by="value"
										text-by="text"
										no-options-text="No se encontraron elementos"
										class="mb-3" />
									<span v-if="!isNotEmpty(certificateData.data.Carrera.clavePlan)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
								</div>
								<div class="mb-3">
									<VaSelect style="width: 100%" label="Periodo de cursamiento" v-model="certificateData.data.Carrera.idTipoPeriodo" :options="opcionesPeriodo" value-by="value" text-by="text" />
									<span v-if="!isNotEmpty(certificateData.data.Carrera.idTipoPeriodo)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
								</div>
							</div>
						</div>
					</div>

					<!-- Sección: Datos de expedición -->
					<div class="package-section">
						<div class="section-title">
							<VaIcon name="event" class="title-icon" />
							Datos de expedición
						</div>
						<div class="package-grid">
							<div class="package-card">
								<div class="gap-3 mb-4">
									<div class="signer-avatar mb-2">
										<VaIcon name="badge" class="signer-icon" />
									</div>
									<div>
										<h3 class="font-semibold text-lg m-0">Tipo y Fecha</h3>
										<p class="text-gray-600 m-0">Configuración del certificado</p>
									</div>
								</div>
								<div class="mb-3">
									<VaSelect
										style="width: 100%"
										label="Tipo de certificado"
										v-model="certificateData.data.Expedicion.idTipoCertificacion"
										:options="opcionesTipoCertificacion"
										value-by="value"
										text-by="text"
										no-options-text="No se encontraron elementos"
										class="mb-3" />
									<span v-if="!isNotEmpty(certificateData.data.Expedicion.idTipoCertificacion)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
								</div>
								<dateControl
									:value="certificateData.data.Expedicion.fecha"
									color="primary"
									:default-value="true"
									:disabled="false"
									label="Fecha de expedición"
									@update-Date="updateFechaExpedicion"
									:verificar-fecha="verificarFecha" />
							</div>

							<div class="package-card">
								<div class="gap-3 mb-4">
									<div class="signer-avatar mb-2">
										<VaIcon name="location_on" class="signer-icon" />
									</div>
									<div>
										<h3 class="font-semibold text-lg m-0">Lugar de Expedición</h3>
										<p class="text-gray-600 m-0">Entidad federativa</p>
									</div>
								</div>
								<div class="mb-3">
									<VaSelect
										style="width: 100%"
										label="Entidad de expedición"
										v-model="idEntidadFederativaExpedicion"
										:options="opcionesEntidadFederativa"
										value-by="value"
										text-by="text"
										no-options-text="No se encontraron elementos" />
									<span v-if="!isNotEmpty(idEntidadFederativaExpedicion)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div v-if="currentTab?.title === 'Estudiante'" class="space-y-6">
					<!-- Sección: Datos del estudiante -->
					<div class="students-section">
						<div class="section-title">
							<VaIcon name="person" class="title-icon" />
							Datos del estudiante
						</div>
						<div class="students-grid">
							<div class="student-card">
								<div class="gap-3 mb-4">
									<div class="student-avatar mb-2">
										<VaIcon name="person" class="student-icon" />
									</div>
									<div>
										<h3 class="font-semibold text-lg m-0">Información Personal</h3>
										<p class="text-gray-600 m-0">Datos básicos del estudiante</p>
									</div>
								</div>
								<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<VaInput style="width: 100%" v-model="certificateData.data.Alumno.numeroControl" label="Matrícula (Identificador del profesionista)" :rules="[validarNumeroControl]" />
										<span v-if="!isNotEmpty(certificateData.data.Alumno.numeroControl)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
									<div>
										<VaInput style="width: 100%" v-model="certificateData.data.Alumno.curp" label="CURP" />
										<span v-if="!isNotEmpty(certificateData.data.Alumno.curp)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
									<div>
										<VaInput style="width: 100%" v-model="certificateData.data.Alumno.nombre" label="Nombre" />
										<span v-if="!isNotEmpty(certificateData.data.Alumno.nombre)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
									<div>
										<VaInput style="width: 100%" v-model="certificateData.data.Alumno.primerApellido" label="Primer apellido" />
										<span v-if="!isNotEmpty(certificateData.data.Alumno.primerApellido)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
									<div>
										<VaInput style="width: 100%" v-model="certificateData.data.Alumno.segundoApellido" label="Segundo apellido" />
									</div>
									<dateControl
										:value="certificateData.data.Alumno.fechaNacimiento"
										color="primary"
										:default-value="true"
										:disabled="false"
										label="Fecha de nacimiento"
										@update-Date="updateFechaNacimiento" />
									<div class="mb-3">
										<VaSelect
											style="width: 100%"
											label="Género"
											v-model="certificateData.data.Alumno.idGenero"
											:options="opcionesGenero"
											value-by="value"
											text-by="label"
											no-options-text="No se encontraron elementos" />
										<span v-if="!isNotEmpty(certificateData.data.Alumno.idGenero)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div v-if="currentTab?.title === 'Asignaturas'" class="space-y-6">
					<!-- Sección: Información general -->
					<div class="package-section">
						<div class="section-title">
							<VaIcon name="list_alt" class="title-icon" />
							Información general
						</div>
						<div class="package-grid">
							<div class="package-card">
								<div class="gap-3 mb-4">
									<div class="signer-avatar mb-2">
										<VaIcon name="collections_bookmark" class="signer-icon" />
									</div>
									<div>
										<h3 class="font-semibold text-lg m-0">Asignaturas del Plan</h3>
										<p class="text-gray-600 m-0">Configuración de asignaturas</p>
									</div>
								</div>
								<div class="grid grid-cols-2 gap-4">
									<div class="gap-3">
										<div class="total-subjects-display">
											<div class="subject-icon">
												<VaIcon name="book" size="small" />
											</div>
											<div class="subject-count">
												<span class="count-number">{{ totalAsignaturas }}</span>
												<span class="count-label">Asignaturas asignadas al alumno en el plan</span>
											</div>
										</div>
									</div>
									<div class="gap-3">
										<div class="total-subjects-display">
											<div class="subject-icon">
												<VaIcon name="book" size="small" />
											</div>
											<div class="subject-count">
												<span class="count-number">{{ certificateData.data.Asignaturas.asignadas }}</span>
												<span class="count-label">Asignaturas cursadas por el alumno</span>
											</div>
										</div>
									</div>
									<div v-if="complementarias > 0" class="gap-3">
										<div class="total-subjects-display">
											<div class="subject-icon">
												<VaIcon name="book" size="small" />
											</div>
											<div class="subject-count">
												<span class="count-number">{{ complementarias }}</span>
												<span class="count-label">Asignaturas complementarias del plan</span>
											</div>
										</div>
									</div>
									<div v-if="asignadasComplementarias > 0" class="gap-3">
										<div class="total-subjects-display">
											<div class="subject-icon">
												<VaIcon name="book" size="small" />
											</div>
											<div class="subject-count">
												<span class="count-number">{{ asignadasComplementarias }}</span>
												<span class="count-label">Asignaturas complementarias cursadas por el alumno</span>
											</div>
										</div>
									</div>
									<div v-if="optativas > 0" class="gap-3">
										<div class="total-subjects-display">
											<div class="subject-icon">
												<VaIcon name="book" size="small" />
											</div>
											<div class="subject-count">
												<span class="count-number">{{ optativas }}</span>
												<span class="count-label">Asignaturas optativas del plan</span>
											</div>
										</div>
									</div>
									<div v-if="asignadasOptativas > 0" class="gap-3">
										<div class="total-subjects-display">
											<div class="subject-icon">
												<VaIcon name="book" size="small" />
											</div>
											<div class="subject-count">
												<span class="count-number">{{ asignadasOptativas }}</span>
												<span class="count-label">Asignaturas optativas cursadas por el alumno</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Sección: Agregar asignaturas -->
					<div class="package-section">
						<div class="section-title">
							<VaIcon name="add_circle" class="title-icon" />
							Agregar asignaturas
						</div>
						<div class="package-card">
							<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
								<VaSelect
									style="width: 100%"
									label="Asignatura"
									v-model="selectedAsignaturaId"
									:options="asignaturasDisponibles"
									value-by="idAsignatura"
									text-by="nombre"
									no-options-text="No se encontraron elementos" />
								<VaSelect
									style="width: 100%"
									label="Observaciones"
									v-model="selectedObservacionId"
									:options="opcionesObservaciones"
									value-by="value"
									text-by="text"
									no-options-text="No se encontraron elementos" />
								<VaInput style="width: 100%" label="Ciclo escolar de cursamiento" v-model="asignaturas.ciclo" :rules="[validarCiclo]" />
							</div>
							<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
								<VaInput style="width: 100%" label="Calificación" v-model="asignaturas.calificacion" :rules="[validarCalificacion]" />
								<div class="flex justify-end gap-2">
									<VaButton v-if="selectedAsignaturaId" color="secondary" class="btn" @click="cancelarEdicion">
										<VaIcon name="cancel" class="mr-2" />
										Cancelar edición
									</VaButton>
									<VaButton color="primary" class="btn btn-primary" @click="agregarAsignatura">
										<VaIcon name="add" class="mr-2" />
										{{ existeAsignatura(selectedAsignaturaId) ? 'Actualizar asignatura' : 'Agregar asignatura' }}
									</VaButton>
								</div>
							</div>
						</div>
					</div>

					<!-- Sección: Listado de asignaturas -->
					<div class="package-section">
						<div class="section-title">
							<VaIcon name="table_chart" class="title-icon" />
							Listado de asignaturas
						</div>
						<div class="package-card">
							<div class="table-container">
								<div class="table-body-scroll">
									<table class="styled-table w100p">
										<thead>
											<tr>
												<th class="info-labelP bg-primary-color bold" scope="col" style="color: white">ID</th>
												<th class="info-label bg-primary-color bold" scope="col" style="color: white">Nombre</th>
												<th class="info-label bg-primary-color bold" scope="col" style="color: white">Observaciones</th>
												<th class="info-label bg-primary-color bold" scope="col" style="color: white">Ciclo</th>
												<th class="info-label bg-primary-color bold" scope="col" style="color: white">Calificación</th>
												<th class="info-label bg-primary-color bold" scope="col" style="color: white">Acciones</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="(asignatura, index) in listaAsignaturas" :key="index">
												<td class="info-value" style="font-size: 10px">{{ asignatura.idAsignatura }}</td>
												<td class="info-value" style="font-size: 10px">{{ asignatura.nombre }}</td>
												<td class="info-value" style="font-size: 10px">{{ asignatura.observaciones }}</td>
												<td class="info-value" style="font-size: 10px">{{ asignatura.ciclo }}</td>
												<td class="info-value" style="font-size: 10px">{{ asignatura.calificacion }}</td>
												<td class="info-value" style="font-size: 9px">
													<VaButton round color="primary" icon="edit" size="small" @click="editarAsignatura(asignatura.idAsignatura)" class="action-button mr-2" />
													<VaButton round color="danger" icon="delete" size="small" @click="eliminarAsignatura(asignatura.idAsignatura)" class="action-button" />
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
			</VaCardContent>

			<!-- Footer con botones -->
			<VaCardActions class="footer-actions" align="right">
				<VaButton color="secondary" class="btn" @click="closeModal"> Cancelar </VaButton>
				<VaButton color="primary" class="btn btn-primary" :disabled="!isInstitutionDataComplete || !isStudentDataComplete || errorsModal.length > 0" @click="guardarCertificado">
					<VaIcon name="save" class="mr-2" />
					Guardar
				</VaButton>
			</VaCardActions>
		</VaCard>

		<!-- Alertas de error -->
		<div v-if="errorsModal.length > 0" class="status-alert">
			<VaAlert color="danger" class="flex items-center justify-center w-full">
				<VaIcon name="error" class="mr-2" />
				<span class="text-base">
					<ul class="list-none p-0 m-0">
						<li v-for="error in errorsModal" :key="error" class="mb-1">{{ error }}</li>
					</ul>
				</span>
			</VaAlert>
		</div>
	</VaModal>
	<BaseModal v-model="showErrorModal" title="Error" :message="errors" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errors }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>

<style lang="css" scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.rounded-t-xl {
	border-top-left-radius: 0.75rem;
	border-top-right-radius: 0.75rem;
}

/* Títulos de sección */
.section-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.title-icon {
	color: #007bff;
	font-size: 1.25rem;
}

/* Avatares */
.signer-avatar,
.student-avatar {
	width: 25px;
	height: 25px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
}

.signer-icon,
.student-icon {
	color: white;
	font-size: 16px;
	line-height: 1;
}

/* Tarjetas */
.package-card,
.signer-card,
.student-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	animation: fadeIn 0.5s ease-in-out;
}

.package-card:hover,
.signer-card:hover,
.student-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

/* Grids */
.package-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 1rem;
}

.signers-grid {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.students-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
	gap: 1rem;
}

/* Botones */
.btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.5rem 1rem;
	border-radius: 8px;
	font-weight: 600;
	font-size: 0.875rem;
	transition: all 0.3s ease;
	border: none;
	cursor: pointer;
	text-decoration: none;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-primary {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
}

.btn-primary:hover {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.action-button {
	transition: all 0.3s ease;
}

.action-button:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Tabla mejorada */
.styled-table {
	width: 100%;
	border-collapse: collapse;
	font-family: Arial, sans-serif;
	background-color: #f9f9f9;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	border: 2px solid #1a4d42 !important;
	border-radius: 8px;
	overflow-y: auto;
	border-top: none;
}

.styled-table thead tr {
	text-align: left;
}

.styled-table th,
.styled-table td {
	padding: 12px 15px;
	border: 1px solid #dddddd;
}

.styled-table tbody tr {
	border-bottom: 1px solid #dddddd;
	transition: background-color 0.2s ease;
}

.styled-table tbody tr:hover {
	background-color: #f0f8ff;
}

.styled-table tbody tr:nth-of-type(even) {
	background-color: #f3f3f3;
}

.styled-table tbody tr:nth-of-type(even):hover {
	background-color: #e6f3ff;
}

.styled-table tbody tr:last-of-type {
	border-bottom: 2px solid #1a4d42 !important;
}

.bg-primary-color {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
}

.table-container {
	position: relative;
	border-radius: 8px;
	overflow: hidden;
}

.table-body-scroll {
	max-height: 300px; /* Puedes ajustar la altura según lo que necesites */
	overflow: auto;
	border: 2px solid #1a4d42;
	border-top: none;
	border-radius: 0 0 8px 8px;
}

/* Responsive */
@media (max-width: 768px) {
	.package-grid,
	.students-grid {
		grid-template-columns: 1fr;
	}

	.package-card,
	.signer-card,
	.student-card {
		padding: 1rem;
	}

	.section-title {
		font-size: 1.125rem;
	}

	.signer-avatar,
	.student-avatar {
		width: 20px;
		height: 20px;
		font-size: 16px;
		line-height: 1;
	}
}

/* Utilidades */
.space-y-6 > * + * {
	margin-top: 1.5rem;
}

.mb-4 {
	margin-bottom: 1rem;
}

.mb-6 {
	margin-bottom: 1.5rem;
}

.mr-2 {
	margin-right: 0.5rem;
}

.gap-2 {
	gap: 0.5rem;
}

.gap-3 {
	gap: 0.75rem;
}

.gap-4 {
	gap: 1rem;
}

.flex {
	display: flex;
}

.items-center {
	align-items: center;
}

.justify-end {
	justify-content: flex-end;
}

.text-white {
	color: white;
}

.text-gray-600 {
	color: #6c757d;
}

.font-semibold {
	font-weight: 600;
}

.font-bold {
	font-weight: 700;
}

.text-lg {
	font-size: 1.125rem;
}

.text-xl {
	font-size: 1.25rem;
}

.text-sm {
	font-size: 0.875rem;
}

.m-0 {
	margin: 0;
}

.p-0 {
	padding: 0;
}

.list-none {
	list-style: none;
}

.bg-gradient-paquete {
	background: linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%);
}
.bg-gradient-paquete-test {
	background: linear-gradient(90deg, #dc2626 0%, #7f1d1d 100%);
}
/* Footer actions */
.footer-actions {
	padding: 0.5rem;
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-top: 2px solid #dee2e6;
	border-radius: 0 0 12px 12px;
}

.footer-actions .flex {
	width: 100%;
}

/* Status alert */
.status-alert {
	position: fixed;
	top: 20px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 9999;
	max-width: 600px;
	width: 90%;
}

/* Total subjects display */
.total-subjects-display {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
	border: 2px solid #2196f3;
	border-radius: 12px;
	padding: 0.75rem 1rem;
	box-shadow: 0 4px 12px rgba(33, 150, 243, 0.15);
	transition: all 0.3s ease;
	min-width: 140px;
}

.total-subjects-display:hover {
	transform: translateY(-2px);
	box-shadow: 0 6px 20px rgba(33, 150, 243, 0.25);
}

.subject-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
	border-radius: 50%;
	color: white;
	box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
}

.subject-count {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
}

.count-number {
	font-size: 1.5rem;
	font-weight: 700;
	color: #1976d2;
	line-height: 1;
}

.count-label {
	font-size: 0.75rem;
	font-weight: 500;
	color: #424242;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}
</style>
